import { useRef, useState, type DragEvent } from 'react';
import { Avatar, Button, useConfirm } from '@/components';
import { formatBytes, formatDate, initials } from '@/lib/format';
import type { AgentWithCounts } from '@/lib/api';
import { INDEXABLE_EXTENSIONS, type KnowledgeSource } from '@shared/types';
import './KnowledgeLibrary.scss';

/**
 * Derived, never written out by hand. The hint once listed PDF while the Worker
 * had no way to read one, so every PDF was stored and then marked "not
 * indexed"; deriving it is what keeps the promise and the capability together —
 * PDF appears here now because the Worker genuinely converts it.
 */
const INDEXABLE_HINT = INDEXABLE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(' · ');

export interface KnowledgeLibraryProps {
  sources: KnowledgeSource[];
  agents: AgentWithCounts[];
  busy: boolean;
  onUpload: (files: File[], agentIds: string[]) => void;
  onAddText: (name: string, content: string, agentIds: string[]) => void;
  onAssign: (sourceId: string, agentIds: string[]) => void;
  onDelete: (sourceId: string) => void;
  /** Resolves to the source's indexed text, or null if it could not be read. */
  onLoadSourceText: (sourceId: string) => Promise<string | null>;
  /** Resolves true when the edit saved, so the row knows it can close. */
  onEditSource: (sourceId: string, name: string, content: string) => Promise<boolean>;
}

const KIND_LABEL: Record<KnowledgeSource['kind'], string> = {
  doc: 'DOC',
  text: 'TEXT',
  skill: 'SKILL',
};

const STATUS_NOTE: Record<KnowledgeSource['status'], string> = {
  pending: 'queued for indexing',
  processing: 'indexing…',
  ready: 'indexed',
  failed: 'not indexed',
};

export const KnowledgeLibrary: React.FC<KnowledgeLibraryProps> = ({
  sources,
  agents,
  busy,
  onUpload,
  onAddText,
  onAssign,
  onDelete,
  onLoadSourceText,
  onEditSource,
}) => {
  // Which agents newly-added sources are assigned to. Empty = all agents.
  const [assignIds, setAssignIds] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  // The row being edited, if any. Its text is fetched on open: the list
  // response deliberately leaves `content` out.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [loadingText, setLoadingText] = useState(false);
  const [saving, setSaving] = useState(false);
  // Which source the in-flight text fetch belongs to, so a slow one cannot
  // drop its text into a row the user has since closed or swapped.
  const editRequest = useRef<string | null>(null);
  const confirm = useConfirm();

  async function removeSource(source: KnowledgeSource) {
    const ok = await confirm({
      title: `Delete “${source.name}”?`,
      body: (
        <>
          Its text and search index are removed, and agents can no longer answer from it.
          {source.hasFile && ' The uploaded file is deleted too.'} This cannot be undone.
        </>
      ),
      confirmLabel: 'Delete source',
    });
    if (ok) onDelete(source.id);
  }

  function toggleAssign(agentId: string | null) {
    if (agentId === null) {
      setAssignIds([]);
      return;
    }
    setAssignIds((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId],
    );
  }

  function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) onUpload(files, assignIds);
  }

  function submitText() {
    if (!title.trim() || !text.trim()) return;
    onAddText(title.trim(), text.trim(), assignIds);
    setTitle('');
    setText('');
  }

  async function startEdit(source: KnowledgeSource) {
    editRequest.current = source.id;
    setEditingId(source.id);
    setEditTitle(source.name);
    setEditText('');
    setLoadingText(true);

    const content = await onLoadSourceText(source.id);
    // The row may have been closed, or another one opened, while that ran.
    if (editRequest.current !== source.id) return;

    setLoadingText(false);
    if (content === null) {
      cancelEdit();
      return;
    }
    setEditText(content);
  }

  function cancelEdit() {
    editRequest.current = null;
    setEditingId(null);
    setEditTitle('');
    setEditText('');
    setLoadingText(false);
  }

  async function saveEdit(sourceId: string) {
    const name = editTitle.trim();
    const content = editText.trim();
    if (!name || !content) return;
    setSaving(true);
    const saved = await onEditSource(sourceId, name, content);
    setSaving(false);
    // A failed save keeps the editor open so nothing typed is thrown away.
    if (saved) cancelEdit();
  }

  /** Toggles one agent's access to an existing source. */
  function toggleSourceAgent(source: KnowledgeSource, agentId: string) {
    const current = source.agentIds;
    // Moving off "All" means starting an explicit list with just this agent.
    if (current.length === 0) {
      onAssign(source.id, [agentId]);
      return;
    }
    const next = current.includes(agentId)
      ? current.filter((id) => id !== agentId)
      : [...current, agentId];
    onAssign(source.id, next);
  }

  return (
    <div className="screen screen--narrow">
      <div className="screen__eyebrow">Source knowledge</div>
      <h1 className="screen__title">Knowledge library</h1>
      <p className="screen__lede">
        Upload documents, paste text, or add a skills file. Each source can be available to every
        agent or assigned to specific ones.
      </p>

      <div className="knowledge__assign-row">
        <div className="knowledge__assign-label">New sources go to</div>
        <button
          type="button"
          className={`chip${assignIds.length === 0 ? ' chip--active' : ''}`}
          onClick={() => toggleAssign(null)}
        >
          All agents
        </button>
        {agents.map((agent) => (
          <button
            key={agent.id}
            type="button"
            className={`chip${assignIds.includes(agent.id) ? ' chip--active' : ''}`}
            onClick={() => toggleAssign(agent.id)}
          >
            {agent.name}
          </button>
        ))}
      </div>

      <label className="knowledge__drop" onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
        <input
          ref={fileInput}
          type="file"
          multiple
          className="knowledge__file-input"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length > 0) onUpload(files, assignIds);
            e.target.value = '';
          }}
        />
        <div className="knowledge__drop-title">Drop documents here, or click to browse</div>
        <div className="knowledge__drop-hint">{INDEXABLE_HINT}</div>
        <div className="knowledge__drop-note">
          PDFs need selectable text — a scan is images, so run it through OCR first. Other file
          types are kept, but their text cannot be read, so agents will not be able to answer
          from them.
        </div>
      </label>

      <div className="knowledge__text-card">
        <div className="field-label">Add text source</div>
        <input
          className="text-input"
          value={title}
          placeholder="Title — e.g. Opening hours &amp; depot addresses"
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          className="text-area knowledge__textarea"
          rows={3}
          value={text}
          placeholder="Paste the text your agents should know…"
          onChange={(e) => setText(e.target.value)}
        />
        <Button size="sm" loading={busy} onClick={submitText}>
          Add source
        </Button>
      </div>

      <div className="knowledge__list">
        {sources.map((source) => {
          // Editing takes over the whole row: the agent toggles and delete
          // button are hidden so neither is hit while reaching for Save.
          if (editingId === source.id) {
            return (
              <div key={source.id} className="source-row source-row--editing">
                <div className="source-row__editor-head">
                  <div className={`source-row__kind source-row__kind--${source.kind}`}>
                    {KIND_LABEL[source.kind]}
                  </div>
                  <div className="source-row__editor-label">Editing source</div>
                </div>

                <input
                  className="text-input"
                  value={editTitle}
                  placeholder="Title"
                  onChange={(e) => setEditTitle(e.target.value)}
                />
                <textarea
                  className="text-area source-row__editor-text"
                  rows={10}
                  value={editText}
                  disabled={loadingText}
                  placeholder={loadingText ? 'Loading…' : 'The text your agents should know…'}
                  onChange={(e) => setEditText(e.target.value)}
                />

                <div className="source-row__editor-actions">
                  <Button
                    size="sm"
                    loading={saving}
                    disabled={loadingText || !editTitle.trim() || !editText.trim()}
                    onClick={() => void saveEdit(source.id)}
                  >
                    Save changes
                  </Button>
                  <Button size="sm" variant="secondary" disabled={saving} onClick={cancelEdit}>
                    Cancel
                  </Button>
                </div>

                <div className="source-row__editor-note">
                  Saving re-indexes this source.
                  {source.hasFile &&
                    ' This edits the indexed text only — the uploaded file is kept as it is.'}
                </div>
              </div>
            );
          }

          return (
            <div key={source.id} className="source-row">
              <div className={`source-row__kind source-row__kind--${source.kind}`}>
                {KIND_LABEL[source.kind]}
              </div>

              <div className="source-row__body">
                <div className="source-row__name">{source.name}</div>
                <div className="source-row__meta">
                  {formatBytes(source.byteSize)} · added {formatDate(source.createdAt)} ·{' '}
                  <span className={`source-row__status source-row__status--${source.status}`}>
                    {STATUS_NOTE[source.status]}
                  </span>
                </div>
                {source.status === 'failed' && source.error && (
                  <div className="source-row__error">{source.error}</div>
                )}
              </div>

              <div className="source-row__agents">
                <button
                  type="button"
                  title="Available to all agents"
                  className={`source-row__all${source.agentIds.length === 0 ? ' source-row__all--active' : ''}`}
                  onClick={() => onAssign(source.id, [])}
                >
                  All
                </button>
                {agents.map((agent) => {
                  const assigned =
                    source.agentIds.length === 0 || source.agentIds.includes(agent.id);
                  return (
                    <button
                      key={agent.id}
                      type="button"
                      title={agent.name}
                      aria-label={`${assigned ? 'Remove' : 'Give'} ${agent.name} access`}
                      className={`source-row__agent${assigned ? '' : ' source-row__agent--off'}`}
                      onClick={() => toggleSourceAgent(source, agent.id)}
                    >
                      <Avatar name={agent.name} colour={agent.colour} size={24} />
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="source-row__edit"
                aria-label={`Edit ${source.name}`}
                onClick={() => void startEdit(source)}
              >
                Edit
              </button>

              <button
                type="button"
                className="source-row__remove"
                aria-label={`Delete ${source.name}`}
                onClick={() => void removeSource(source)}
              >
                &times;
              </button>
            </div>
          );
        })}
      </div>

      {sources.length === 0 && (
        <div className="empty-note">No sources yet — upload a document or add text above.</div>
      )}
    </div>
  );
};

/** Kept exported so the sidebar avatar fallback and tests share one helper. */
export { initials };
export default KnowledgeLibrary;
